import { useEffect, useState } from 'react';

import { useGetNewAccessTokenMutation } from '@Services/auth.service';
import TokenService from '@Services/token.service';
import { logout, setCredentials } from '@Slices/auth.slice';

import { useAppDispatch, useAppSelector } from './useStore.hook';

const useAuthStatus = (): { isLoading: boolean; isLoggedIn: boolean } => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [getNewAccessToken] = useGetNewAccessTokenMutation();
  const [fetchAccessToken, setFetchAccessToken] = useState<
    boolean | undefined
  >();
  const authState = useAppSelector((state) => state.auth);

  /**
   * To get a new access token via a valid refresh token once the access token has either expired/deleted
   */
  useEffect(() => {
    if (fetchAccessToken) {
      const fetchToken = async () => {
        const rToken = TokenService.getRefreshToken();
        const userRole = TokenService.getUserRole();
        if (rToken) {
          const response = await getNewAccessToken(rToken).unwrap();
          dispatch(
            setCredentials({
              ...response,
              isLoggedIn: true,
              role: userRole,
            })
          );
          setIsLoggedIn(true);
          setIsLoading(false);
          setFetchAccessToken(false);
        } else {
          dispatch<{
            payload: undefined;
            type: string;
          }>(logout());
        }
      };
      fetchToken()
        .then(() => {})
        .catch(() => {
          setIsLoggedIn(false);
          setIsLoading(false);
        });
    }
  }, [fetchAccessToken, dispatch, getNewAccessToken]);

  /**
   * Check the current access token and refresh token for validity and update global state accordingly
   */
  useEffect(() => {
    const aToken = TokenService.getAccessToken();
    const rToken = TokenService.getRefreshToken();

    if (!!aToken && !TokenService.isTokenExpired(aToken)) {
      // if the authState in store is not updated with the current status of user
      // this will run when the user directly lands as a logged in user
      if (!authState.accessToken) {
        dispatch(
          setCredentials({
            accessToken: aToken,
            refreshToken: rToken,
            isLoggedIn: true,
            role: TokenService.getUserRole(),
          })
        );
        setIsLoggedIn(true);
      }
      setIsLoading(false);
    } else if (!!rToken && !TokenService.isTokenExpired(rToken)) {
      setFetchAccessToken(true);
    } else if (rToken && TokenService.isTokenExpired(rToken)) {
      dispatch<{
        payload: undefined;
        type: string;
      }>(logout());
      setIsLoggedIn(false);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [dispatch, authState.accessToken]);

  return { isLoggedIn, isLoading };
};

export default useAuthStatus;
